import {
    ScheduleApplicationPeriodRole,
    ScheduleJeSubtype,
} from "@common/types/domains/accounting/schedule";

/**
 * The subtypes a manual deferred schedule's opening entry and its later
 * adjustments carry: the one entry that moves the amount onto the holding
 * account, which a linked source gets by re-coding instead.
 */
export const SCHEDULE_OPENING_ENTRY_SUBTYPES: readonly ScheduleJeSubtype[] = [
    ScheduleJeSubtype.addition,
];

export function isScheduleOpeningEntry(entry: {
    subtype: ScheduleJeSubtype;
}): boolean {
    return SCHEDULE_OPENING_ENTRY_SUBTYPES.includes(entry.subtype);
}

/**
 * A reversal that cancels an opening entry moved deferral, not recognition,
 * so neither the recognized amount nor the remaining balance may count it.
 */
export function isScheduleOpeningReversal(entry: {
    subtype: ScheduleJeSubtype;
    reversedSubtype: ScheduleJeSubtype | null;
}): boolean {
    return (
        entry.subtype === ScheduleJeSubtype.reversal &&
        entry.reversedSubtype != null &&
        isScheduleOpeningEntry({ subtype: entry.reversedSubtype })
    );
}

export interface ScheduleBalanceAdjustmentCandidate {
    subtype: ScheduleJeSubtype;
    periodId: string | null;
    periodRole: ScheduleApplicationPeriodRole | null;
    reversesPeriodId: string | null;
    reversedSubtype: ScheduleJeSubtype | null;
}

/**
 * A lifecycle entry that moves the holding balance on its own, outside the
 * ladder: a change's catch-up, a stop settled by recognizing or
 * reclassifying the rest, and the reversal that later cancels either. A
 * ladder month's recognition and its reversal are counted through the
 * period rows instead, so a reversal counts here only when the row it
 * cancels is not a ladder row (`ladderPeriodIds`), or when it cancels no
 * row at all: a legacy period-less reversal, or a reversal row written for
 * a legacy period-less entry. A reversed opening entry moved deferral, not
 * recognition, so it stays out.
 */
export function isScheduleBalanceAdjustmentEntry(options: {
    entry: ScheduleBalanceAdjustmentCandidate;
    ladderPeriodIds: ReadonlySet<string>;
}): boolean {
    const { entry } = options;
    switch (entry.subtype) {
        case ScheduleJeSubtype.catchUp:
        case ScheduleJeSubtype.reclassification:
            return true;
        case ScheduleJeSubtype.recognized:
            return (
                entry.periodId === null ||
                entry.periodRole === ScheduleApplicationPeriodRole.lifecycle
            );
        case ScheduleJeSubtype.reversal:
            if (isScheduleOpeningReversal(entry)) {
                return false;
            }
            if (entry.periodId === null) {
                return true;
            }
            return (
                entry.periodRole === ScheduleApplicationPeriodRole.reversal &&
                (entry.reversesPeriodId === null ||
                    !options.ladderPeriodIds.has(entry.reversesPeriodId))
            );
        default:
            return false;
    }
}

/**
 * A balance adjustment that also moves what the schedule has recognized: a
 * reclassification moves the balance to another account without recognizing
 * anything, so it stays out.
 */
export function isScheduleRecognitionAdjustmentEntry(options: {
    entry: ScheduleBalanceAdjustmentCandidate;
    ladderPeriodIds: ReadonlySet<string>;
}): boolean {
    return (
        options.entry.subtype !== ScheduleJeSubtype.reclassification &&
        isScheduleBalanceAdjustmentEntry(options)
    );
}
